import type { DateRangeSource } from "@angelblanco/v-calendar/dist/types/src/utils/date/range.js"
import type {
  DayInMonth,
  MonthInYear,
} from "@angelblanco/v-calendar/dist/types/src/utils/date/helpers.js"

import * as R from "remeda"
import * as dt from "date-fns"
import { createGlobalState, useStorage } from "@vueuse/core"
import { z } from "zod"

import { format, zonedTimeToUtc } from "date-fns-tz"
import { Freq, HorizonLabel } from "./schemas"

export const tz = "Europe/Brussels"

export function getDateAsFromTz(date: Date, timezone?: string): Date {
  // This function transforms a datetime that is localized in the user browser tz (ex 00:00 in Réunion TZ)
  // to the equivalent datetime in a target TZ (ex: 00:00 in CET)
  // Warning: this is not a convertion
  // This should be used when the user pick a date (that is localized with it's browser tz)
  // to a target tz (the one of the target business object (ex: the site / contract tz))
  return zonedTimeToUtc(`${format(date, "yyyy-MM-dd")} 00:00:00`, timezone ?? tz)
}

// The timezone shared by all the sites, falling back to the default one when
// they span several timezones (or there is no site)
export function sitesTimezone(sites: { region: { tz: string } }[]): string {
  const timezones = R.unique(sites.map((site) => site.region.tz))
  return timezones.length === 1 ? (timezones[0] ?? tz) : tz
}

export function toDateTZ(timestamp: string | number | Date): Date {
  let sainTimestamp: Date

  if (R.isNumber(timestamp)) {
    sainTimestamp = dt.fromUnixTime(timestamp)
  } else if (R.isString(timestamp)) {
    sainTimestamp = dt.parseISO(timestamp)
  } else {
    sainTimestamp = timestamp
  }
  return sainTimestamp
}

/**
 * ISO date (with the time when `full`), whatever the user's preference: for keys, stored values and
 * exports. Dates shown to the user go through displayDate.
 */
export function toISODate(
  timestamp: Date | string | number | undefined | null,
  full?: boolean,
): string {
  if (!timestamp) {
    return ""
  }

  if (R.isString(timestamp)) {
    timestamp = dt.parseISO(timestamp)
  } else if (R.isNumber(timestamp)) {
    timestamp = dt.fromUnixTime(timestamp)
  }

  const fmt = full ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"
  return dt.format(timestamp, fmt)
}

export const DateFormat = z.enum(["iso", "locale"])
export type DateFormat = z.infer<typeof DateFormat>

/** Date format picked in the settings page, shared by every displayDate call. */
export const useDateFormatPreference = createGlobalState(() => {
  const stored = useStorage<string>(StorageKeys.DateFormat, DateFormat.enum.iso)

  return computed<DateFormat>({
    get: () => DateFormat.catch(DateFormat.enum.iso).parse(stored.value),
    set: (value) => (stored.value = value),
  })
})

/**
 * Date (with the time when `full`) as shown to the user, by default in the format picked in the
 * settings page: ISO, or the numeric date of the UI language with a 24h time. Keys, stored values
 * and exports must stay ISO and use toISODate instead.
 */
export function displayDate(
  timestamp: Date | string | number | undefined | null,
  full?: boolean,
  format: DateFormat = useDateFormatPreference().value,
): string {
  if (!timestamp) {
    return ""
  }

  if (format === DateFormat.enum.iso) {
    return toISODate(timestamp, full)
  }

  const time: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }

  return new Intl.DateTimeFormat(useNuxtApp().$i18n.locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(full ? time : {}),
  }).format(toDateTZ(timestamp))
}

/**
 * Check if a date is within the last N days
 */
export function isWithinLastNDays(
  timestamp: Date | string | number | undefined | null,
  days: number,
): boolean {
  if (R.isNullish(timestamp)) {
    return false
  }

  let date: Date
  if (R.isString(timestamp)) {
    date = dt.parseISO(timestamp)
  } else if (R.isNumber(timestamp)) {
    date = dt.fromUnixTime(timestamp)
  } else {
    date = timestamp
  }

  const daysAgo = dt.differenceInDays(new Date(), date)
  return daysAgo >= 0 && daysAgo <= days
}

export function diffToDuration(end: string | Date, start: string | Date) {
  let diff = dt.differenceInMilliseconds(toDateTZ(end), toDateTZ(start)) / 1000

  // calculate (and subtract) whole days
  const days = Math.floor(diff / 86400)
  diff -= days * 86400

  // calculate (and subtract) whole hours
  const hours = Math.floor(diff / 3600) % 24
  diff -= hours * 3600

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(diff / 60) % 60

  return `${days}d ${hours}h ${minutes}m`
}

/**
 * Compact duration skipping zero units, e.g. "2d 30m" instead of "2d 0h 30m".
 */
export function formatCompactDuration(start: Date, end: Date) {
  const totalMinutes = dt.differenceInMinutes(end, start)
  const parts = [
    { value: Math.floor(totalMinutes / 1440), unit: "d" },
    { value: Math.floor((totalMinutes % 1440) / 60), unit: "h" },
    { value: totalMinutes % 60, unit: "m" },
  ].filter(({ value }) => value > 0)

  if (parts.length === 0) return "0m"

  return parts.map(({ value, unit }) => `${value}${unit}`).join(" ")
}

/**
 * Period with times, only repeating the date of the end when it differs from the start,
 * e.g. "2024-05-12 08:00 → 14:30" or "2024-05-12 08:00 → 2024-05-13 10:00".
 */
export function formatPeriod(start: Date, end: Date) {
  const endLabel = dt.isSameDay(start, end) ? dt.format(end, "HH:mm") : displayDate(end, true)

  return `${displayDate(start, true)} → ${endLabel}`
}

/**
 * Generate an Interval for the "Year to Date",
 * correctly enlarging when it is still the beginning of the year (not yet march).
 */
export function defaultYTDInterval(): { start: Date; end: Date } {
  const nMonthsSlide = 3
  const now = new Date()
  let start: Date
  let end: Date
  if (now < dt.addMonths(dt.startOfYear(now), nMonthsSlide)) {
    start = dt.startOfYear(dt.addYears(now, -1))
    end = dt.startOfMonth(dt.addMonths(now, 1))
  } else {
    start = dt.startOfYear(now)
    end = dt.startOfYear(dt.addYears(now, 1))
  }

  return { start, end }
}

const now = () => new Date()

export const dth: Record<HorizonLabel, () => [Date, Date]> = {
  [HorizonLabel.enum.ytd]: () => {
    if (now().getMonth() === 0) {
      // if in january, return from last year to today
      return [dt.addYears(dt.startOfYear(now()), -1), dt.startOfDay(now())]
    } else {
      return [dt.startOfYear(now()), dt.startOfDay(now())]
    }
  },
  [HorizonLabel.enum.yt_last_month]: () => {
    if (now().getMonth() === 0) {
      // if in january, return last year instead
      return [dt.addYears(dt.startOfYear(now()), -1), dt.startOfYear(now())]
    } else {
      return [dt.startOfYear(now()), dt.startOfMonth(now())]
    }
  },
  [HorizonLabel.enum.yt_quarter]: () => {
    if (now().getMonth() < 2) {
      return [dt.startOfYear(now()), dt.addMonths(dt.startOfYear(now()), 3)]
    } else {
      return [dt.startOfYear(now()), dt.startOfQuarter(now())]
    }
  },
  [HorizonLabel.enum.last_year]: () => [
    dt.addYears(dt.startOfYear(now()), -1),
    dt.startOfYear(now()),
  ],
  [HorizonLabel.enum.current_year]: () => [
    dt.startOfYear(now()),
    dt.addYears(dt.startOfYear(now()), 1),
  ],
  [HorizonLabel.enum.rolling_year]: () => [
    dt.addDays(dt.startOfDay(now()), -365),
    dt.startOfDay(now()),
  ],
  // Months
  [HorizonLabel.enum.current_month]: () => [
    dt.startOfMonth(now()),
    dt.addMonths(dt.startOfMonth(now()), 1),
  ],
  [HorizonLabel.enum.last_month]: () => [
    dt.addMonths(dt.startOfMonth(now()), -1),
    dt.startOfMonth(now()),
  ],
  [HorizonLabel.enum.rolling_month]: () => [
    dt.addDays(dt.startOfDay(now()), -30),
    dt.startOfDay(now()),
  ],
  // Weeks
  [HorizonLabel.enum.current_week]: () => [
    dt.previousMonday(dt.startOfDay(now())),
    dt.nextMonday(dt.startOfDay(now())),
  ],
  [HorizonLabel.enum.last_week]: () => [
    dt.previousMonday(dt.previousMonday(dt.startOfDay(now()))),
    dt.previousMonday(dt.startOfDay(now())),
  ],
  [HorizonLabel.enum.rolling_week]: () => [
    dt.addDays(dt.startOfDay(now()), -7),
    dt.startOfDay(now()),
  ],
  // Custom
  [HorizonLabel.enum.custom]: () => [dt.startOfDay(now()), dt.startOfDay(now())],
  // Days
  [HorizonLabel.enum.today]: () => [dt.startOfDay(now()), dt.addDays(dt.startOfDay(now()), 1)],
  [HorizonLabel.enum.yesterday]: () => [dt.addDays(dt.startOfDay(now()), -1), dt.startOfDay(now())],
  // Rolling PRs
  [HorizonLabel.enum.rolling_0_30]: () => [
    dt.addDays(dt.startOfDay(now()), -30),
    dt.startOfDay(now()),
  ],
  [HorizonLabel.enum.rolling_0_90]: () => [
    dt.addDays(dt.startOfDay(now()), -90),
    dt.startOfDay(now()),
  ],
  [HorizonLabel.enum.rolling_30_60]: () => [
    dt.addDays(dt.startOfDay(now()), -60),
    dt.addDays(dt.startOfDay(now()), -30),
  ],
  [HorizonLabel.enum.rolling_30_120]: () => [
    dt.addDays(dt.startOfDay(now()), -120),
    dt.addDays(dt.startOfDay(now()), -30),
  ],
}

export const mapFreqDisabledDates: Partial<Record<Freq, DateRangeSource[]>> = {
  [Freq.enum["1MS"]]: [
    { repeat: { days: Array.from({ length: 30 }, (_, i) => i + 2) as DayInMonth[] } },
  ],
  [Freq.enum["1YS"]]: [
    { repeat: { days: Array.from({ length: 30 }, (_, i) => i + 2) as DayInMonth[] } },
    { repeat: { months: Array.from({ length: 11 }, (_, i) => i + 2) as MonthInYear[] } },
  ],
}
function getCurrentYearOrLastSixMonths(): Date {
  const currentYearStart = dt.startOfYear(new Date())
  const sixMonthsAgo = dt.sub(dt.startOfMonth(new Date()), { months: 6 })
  return dt.min([currentYearStart, sixMonthsAgo])
}

export const CurrentYearOrLastSixMonthsHorizonSchema = z.object({
  start: z.coerce.date().default(getCurrentYearOrLastSixMonths()),
  end: z.coerce.date().default(dt.addDays(dt.startOfDay(new Date()), 1)),
})
